import "./DueDateButton.scss";

import clsx from "clsx";
import { differenceInCalendarDays, format, isPast } from "date-fns";
import { type FC, Suspense, lazy, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import formatDistanceToNow from "../../../i18n/formatDistanceToNow";

import { showPageDisabled } from "./groups/helpers";
import { getLateSubmissionDueDate, isInLateSubmissionWindow } from "./helpers";
import {
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "./interaction-mode/helpers";

import { PageType } from "components/server-types";
import { DAY_AND_TIME_FORMAT, relativeDayAndTimeFormat } from "i18n/helpers";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { Chip } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { Popover, PopoverButton, PopoverPanel } from "mds/components/Popover";
import { CalendarIcon, ChevronDownIcon, LoadingDotsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  sectionSelectors,
  selectCurrentSectionIsLinked,
  selectIsEditModeEnabled,
} from "store/selectors";

const DueDateForm = lazy(() => import("components/forms/DueDateForm"));

type DueDateButtonProps = {
  page: PageType;
  readOnly?: boolean;
  kind?: "page" | "list-item";
  shortText?: boolean;
  clickStopPropagation?: boolean;
};

const UPDATE_DISTANCE_INTERVAL = 30000;

type DisplayProps = {
  dueDateString: string;
  shortDueDateString?: string;
  dueDateTitle: string;
  isWithinLateWindow?: boolean;
};

const defaultLateSubmissionDisplayInfo = (
  lateDueDate: Date | null | undefined,
  distance: string,
): DisplayProps => {
  const dueDateTitle = lateDueDate
    ? t(`due_date.tooltips.accepting_late_submissions_date`, {
        date: format(lateDueDate, DAY_AND_TIME_FORMAT),
      })
    : t(`due_date.tooltips.accepting_late_submissions_indefinite`, { distance });

  return {
    dueDateString: t("due_date.accepting_late_submissions"),
    shortDueDateString: t("due_date.accepting_late_submissions_short"),
    dueDateTitle,
  };
};

const defaultDisplayInfo = (dueDate: Date | null | undefined, distance: string): DisplayProps => {
  const daysDifference = dueDate ? Math.abs(differenceInCalendarDays(dueDate, new Date())) : 0;
  const shouldShowDistance = daysDifference <= 7;

  const dueDateString = dueDate
    ? shouldShowDistance
      ? t("due_date.due_with_distance", {
          date: dueDate,
          distance,
        })
      : t("due_date.due_without_distance", {
          date: dueDate,
        })
    : t("due_date.title");

  return {
    dueDateString,
    shortDueDateString: dueDate ? relativeDayAndTimeFormat(dueDate) : t("due_date.title"),
    dueDateTitle: dueDateString,
  };
};

const getDueDateDisplayInfo = (
  page: PageType,
  dueDate: Date | undefined,
  distance: string,
  shortText: boolean,
): DisplayProps => {
  const lateDueDate =
    dueDate && page.late_submission_interval ? getLateSubmissionDueDate(page) : undefined;
  const pastLateDueDate = lateDueDate && isPast(lateDueDate);
  const isWithinLateWindow = isInLateSubmissionWindow(page);

  const {
    dueDateString,
    shortDueDateString,
    dueDateTitle: baseDueDateTitle,
  } = isWithinLateWindow
    ? defaultLateSubmissionDisplayInfo(lateDueDate, distance)
    : defaultDisplayInfo(dueDate, distance);

  const dueDateTitle = pastLateDueDate
    ? t("due_date.tooltips.accepted_late_submissions", { distance })
    : baseDueDateTitle;

  return {
    dueDateString: shortText && dueDate ? relativeDayAndTimeFormat(dueDate) : dueDateString,
    shortDueDateString,
    dueDateTitle,
    isWithinLateWindow,
  };
};

export const DueDateButton: FC<DueDateButtonProps> = ({
  page,
  readOnly,
  kind = "page",
  shortText,
  clickStopPropagation,
}) => {
  const dueDate = useMemo(() => (page.due_at ? new Date(page.due_at) : undefined), [page.due_at]);
  const pageIsReleased = Boolean(page.released_at);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  // Should always be editable in linked sections
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled) || isInLinkedSection;
  const section = useAppSelector((s) => sectionSelectors.selectById(s, page.section_id));
  const { canAuthorSection } = useCapabilities();
  const isListItem = kind === "list-item";
  const showDisabled = isListItem && showPageDisabled(page);

  const [distance, setDistance] = useState<string>(
    dueDate ? formatDistanceToNow(dueDate, { addSuffix: true }) : "",
  );

  const { dueDateString, shortDueDateString, dueDateTitle, isWithinLateWindow } = useMemo(
    () => getDueDateDisplayInfo(page, dueDate, distance, shortText ?? false),
    [page, dueDate, distance, shortText],
  );

  useEffect(() => {
    if (dueDate) {
      setDistance(formatDistanceToNow(dueDate, { addSuffix: true }));

      const intervalId = setInterval(() => {
        // update `distance` string every 30s
        setDistance(formatDistanceToNow(dueDate, { addSuffix: true }));
      }, UPDATE_DISTANCE_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [dueDate]);

  if (!readOnly && canAuthorSection && isEditModeEnabled) {
    return (
      <Popover
        className={clsx(
          "due-date-button relative bg-white text-black-tint-40",
          isListItem ? "rounded-md" : "",
        )}
      >
        {isListItem ? (
          // For some reason, using the new button here causes integration test failures around finding the toolbar selector
          // TODO: Investigate and fix this, then switch to the new Button component
          <PopoverButton
            as={LegacyButton}
            className={clsx(
              "due-date-button-list-item list-item-chip",
              isWithinLateWindow && "within-late-window bg-orange-tint-90 text-orange-shade-40",
            )}
            kind="secondary"
            size="xs"
            title={dueDateTitle}
          >
            <IconText iconEnd={<ChevronDownIcon />} text={dueDateString} />
          </PopoverButton>
        ) : (
          <PopoverButton
            as={Button}
            className="gap-1.5"
            kind={isWithinLateWindow ? "chipOrange" : "chipWhite"}
            size="xs"
            title={dueDateTitle}
          >
            <IconText
              className="gap-1.5 text-xs"
              containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
              containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
              iconStart={<CalendarIcon size="xs" />}
              shortText={shortDueDateString}
              text={dueDateString}
            />
            <ChevronDownIcon size="xs" />
          </PopoverButton>
        )}

        <PopoverPanel
          anchor="bottom"
          onClick={clickStopPropagation ? (e) => e.stopPropagation() : undefined}
        >
          {({ close }) => (
            <>
              {pageIsReleased && (
                <div className="w-80 rounded border-orange bg-orange-tint-90 p-2">
                  {dueDate && isPast(dueDate)
                    ? t("due_date.reopen_submissions_warning")
                    : t("due_date.page_released_warning")}
                </div>
              )}

              <Suspense fallback={<LoadingDotsIcon />}>
                <DueDateForm
                  close={close}
                  date={dueDate}
                  lateInterval={page.late_submission_interval ?? undefined}
                  lockAtDueDate={page.should_lock_at_due_date}
                  pageId={page.id}
                  sectionDefaultLateInterval={section?.default_late_submission_interval}
                />
              </Suspense>
            </>
          )}
        </PopoverPanel>
      </Popover>
    );
  }

  if (dueDate) {
    const text = <span title={dueDateTitle}>{dueDateString}</span>;

    return isListItem ? (
      <div
        className={twMerge(
          "due-date-button due-date-button-list-item list-item-chip flex cursor-default items-center justify-center gap-1 whitespace-nowrap border-black-tint-90 bg-black-tint-97 px-2 py-[3px]",
          showDisabled ? "text-black-tint-55" : "text-black-tint-40",
          isWithinLateWindow && "within-late-window !bg-orange-tint-90 !text-orange-shade-40",
        )}
      >
        {text}
      </div>
    ) : (
      <Chip kind={isWithinLateWindow ? "orange" : "white"}>
        <IconText
          className="gap-1.5"
          containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
          containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
          iconStart={<CalendarIcon className="gap-1.5" size="xs" />}
          shortText={shortDueDateString ?? undefined}
          text={dueDateString}
        />
      </Chip>
    );
  }

  return null;
};

DueDateButton.displayName = "DueDateButton";
